import { useEffect, useRef, useState } from "react";

interface ICaptchaProps {
  onVerify: (token: string) => void;
  onExpired?: () => void;
  onError?: () => void;
}

const isLocalhost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

export function Captcha({ onVerify, onExpired, onError }: ICaptchaProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<number | null>(null);

  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

  // Auto-verify on localhost
  useEffect(() => {
    if (isLocalhost) {
      onVerify("localhost-bypass-token");
    }
  }, [onVerify]);

  useEffect(() => {
    // Skip loading reCAPTCHA script on localhost
    if (isLocalhost) {
      return undefined;
    }

    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/enterprise.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (isLoaded && captchaRef.current && !widgetIdRef.current) {
      const { grecaptcha } = window as typeof window & {
        grecaptcha?: {
          render: (
            container: HTMLElement,
            parameters: {
              sitekey: string;
              callback: (token: string) => void;
              "expired-callback"?: () => void;
              "error-callback"?: () => void;
              action?: string;
            }
          ) => number;
          enterprise?: {
            ready: (callback: () => void) => void;
            render: (
              container: HTMLElement,
              parameters: {
                sitekey: string;
                callback: (token: string) => void;
                "expired-callback"?: () => void;
                "error-callback"?: () => void;
              }
            ) => number;
          };
        };
      };

      // Enterprise API requires ready callback
      if (grecaptcha?.enterprise?.ready) {
        grecaptcha.enterprise.ready(() => {
          const renderMethod =
            grecaptcha?.enterprise?.render || grecaptcha?.render;

          if (renderMethod && captchaRef.current) {
            try {
              widgetIdRef.current = renderMethod(captchaRef.current, {
                sitekey: RECAPTCHA_SITE_KEY,
                callback: onVerify,
                "expired-callback": onExpired,
                "error-callback": onError,
              });
            } catch (error) {
              console.error("Failed to render reCAPTCHA:", error);
            }
          }
        });
      }
    }
  }, [isLoaded, onVerify, onExpired, onError, RECAPTCHA_SITE_KEY]);

  // On localhost, show a dev message instead of CAPTCHA
  if (isLocalhost) {
    return (
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            fontSize: "0.75rem",
            color: "#9ca3af",
            fontStyle: "italic",
          }}
        >
          Development mode: reCAPTCHA verification bypassed
        </div>
      </div>
    );
  }

  if (!RECAPTCHA_SITE_KEY) {
    return (
      <div
        style={{
          padding: "1rem",
          border: "1px solid #ff6b6b",
          borderRadius: "4px",
          backgroundColor: "#ffe0e0",
          color: "#c92a2a",
        }}
      >
        reCAPTCHA Enterprise is not configured. Please add
        REACT_APP_RECAPTCHA_SITE_KEY to your environment variables.
      </div>
    );
  }

  // Show reCAPTCHA widget
  return (
    <div style={{ textAlign: "center" }}>
      <div ref={captchaRef} />
    </div>
  );
}

export default Captcha;
